import { Box, CircularProgress, Container, Typography, useTheme } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import {
  DataGridPremium,
  esES,
  frFR,
  gridClasses,
  GridColumnVisibilityModel,
  GridEventListener,
  GridOverlay,
  GridPaginationModel,
  GridToolbar,
} from '@mui/x-data-grid-premium';
import { SCREEN_HEIGHT } from 'constants/ScreenHeight';
import useScreenHeight from 'hooks/useScreenHeight';
import { useState,useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const ODD_OPACITY = 0.2;

const StripedDataGrid = styled(DataGridPremium)(({ theme }) => ({
  width: '100%',
  backgroundColor: 'white',
  '& .MuiDataGrid-row:hover': {
    cursor: 'pointer',
  },
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.grey[200],
    '&:hover, &.Mui-hovered': {
      backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
    '&.Mui-selected': {
      backgroundColor: alpha(
        theme.palette.primary.main,
        ODD_OPACITY + theme.palette.action.selectedOpacity,
      ),
      '&:hover, &.Mui-hovered': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          ODD_OPACITY +
            theme.palette.action.selectedOpacity +
            theme.palette.action.hoverOpacity,
        ),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: alpha(
            theme.palette.primary.main,
            ODD_OPACITY + theme.palette.action.selectedOpacity,
          ),
        },
      },
    },
  },
}));

export default function DataGridCustom({
  columns,
  rows,
  loading,
  sortModel,
  onRowClick,
  filterModel,
  getRowId,
  columnVisibilityModel,
  autoHeight = false,
  fullWidth = false,
  noDataText = undefined,
  rowCount,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onSortModelChange,
}: {
  columns: any;
  rows: any;
  loading?: boolean;
  sortModel?: any;
  onRowClick?: any;
  filterModel?: any;
  getRowId?: any;
  columnVisibilityModel?: any;
  fullWidth?: boolean;
  autoHeight?: boolean;
  noDataText?: string;
  rowCount?: number;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onSortModelChange?: (model: any) => void;
}): JSX.Element {
  const theme = useTheme();
  const [muiTableKey] = useState(0);
  const screenHeight = useScreenHeight();
  const { i18n, t } = useTranslation();
  const usedLanguage = i18n.language;

  const LoadingOverlay = () => (
    <GridOverlay>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <CircularProgress size={28} />
        <Typography variant="body2">{t('shared.loadingData')}</Typography>
      </Box>
    </GridOverlay>
  );

  const handleRowClick: GridEventListener<'rowClick'> = (params: any) => {
    onRowClick?.(params.row);
  };

  const [customColumnVisibilityModel, setCustomColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(columnVisibilityModel);


  const handleDataGridTexts = useMemo(()=> {
    let baseLanguage = {};
    if(usedLanguage?.startsWith('fr')) {
      baseLanguage = frFR.components.MuiDataGrid.defaultProps.localeText;
    } else if(usedLanguage?.startsWith('es')){
      baseLanguage = esES.components.MuiDataGrid.defaultProps.localeText;
    }
    if(noDataText){
      baseLanguage = {...baseLanguage,noRowsLabel: noDataText  }
    }
    return baseLanguage;
  }, [noDataText, usedLanguage])

  return (
    <Container
      sx={{
        maxWidth: 'unset!important',
        p: fullWidth ? '0!important' : `${theme.spacing(2)}!important`,
        pt: `0px!important`,
        height: screenHeight - SCREEN_HEIGHT.HEADER,
        '& .MuiDataGrid-columnHeaderTitle': {
          fontWeight: 'bold',
        },
      }}
    >
      <StripedDataGrid
        localeText={handleDataGridTexts}
        key={muiTableKey}
        rows={rows}
        rowHeight={30}
        columnHeaderHeight={35}
        columns={columns}
        loading={loading}
        getRowId={getRowId}
        columnVisibilityModel={customColumnVisibilityModel}
        onColumnVisibilityModelChange={(newModel) =>
          setCustomColumnVisibilityModel(newModel)
        }
        onRowClick={handleRowClick}
        initialState={{
          sorting: { sortModel: onSortModelChange ? undefined : sortModel },
          filter: { filterModel: { items: filterModel || [] } },
        }}
        components={{
          Toolbar: GridToolbar,
          LoadingOverlay: LoadingOverlay,
        }}
        getRowClassName={(params: any) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        {...(onSortModelChange
          ? {
              sortingMode: 'server' as const,
              onSortModelChange,
              sortModel,
            }
          : {})}
        {...(onPageChange
          ? {
              pagination: true,
              paginationMode: 'server' as const,
              rowCount,
              paginationModel: { page: page ?? 0, pageSize: pageSize ?? 100 },
              onPaginationModelChange: (model: GridPaginationModel) => {
                if (model.page !== page) {
                  onPageChange(model.page);
                }
                if (model.pageSize !== pageSize) {
                  onPageSizeChange?.(model.pageSize);
                }
              },
              pageSizeOptions: [50, 100, 200],
            }
          : {
              hideFooter: true,
              hideFooterPagination: true,
              hideFooterSelectedRowCount: true,
            })}
        {...(autoHeight && { autoHeight })}
      />
    </Container>
  );
}
